import { Spinner } from "../../Layout/Loaders/Spinner";
import { DialogContext, DialogStateContext } from "./DialogContext";
import { FC, ReactNode, Suspense, useCallback, useState, useMemo } from "react";

export type DialogContextProps = {
  dialogs: Record<string, Dialog>;
  createDialog: (uuid: string, data: DialogInput) => void;
  removeDialog: (uuid: string) => void;
  depth: number;
};

export type Dialog<TParams = Record<string, any>, TRecord = any> = {
  Component: FC<TParams>;
  props: TParams;
  isOpen: boolean;
  close: () => Promise<void>;
  uuid: string;
  resolve?: (value: TRecord) => void;
  reject?: (reason: any) => void;
};

export type DialogInput = Omit<Dialog, "close" | "uuid">;

export type DialogState<TRecord> = Omit<
  Dialog<never, TRecord>,
  "props" | "Component"
> & {
  depth: number;
  remove: () => void;
};

// Extracted spinner component to prevent re-creation on every render
const DialogSpinner = () => (
  <div className="fixed inset-0 flex w-screen items-center justify-center bg-zinc-950/25 px-2 py-2 focus:outline-0">
    <Spinner className="text-blue-500" />
  </div>
);

export function DialogProvider({
  depth = 0,
  children,
}: {
  depth?: number;
  children: ReactNode;
}) {
  const [dialogs, setDialogs] = useState<Record<string, Dialog>>({});

  const closeDialog = useCallback(async (dialogId: string) => {
    setDialogs((oldDialogs) => {
      // If the dialog has unmounted, don't try to close it
      if (!oldDialogs[dialogId]) {
        return oldDialogs;
      }

      // Fix: Create new object instead of mutating
      return {
        ...oldDialogs,
        [dialogId]: {
          ...oldDialogs[dialogId],
          isOpen: false,
        },
      };
    });

    // Wait for the animation to complete (100ms from dialog.tsx)
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Remove the dialog from state so it unmounts completely
    setDialogs((oldDialogs) => {
      const copy = { ...oldDialogs };
      delete copy[dialogId];
      return copy;
    });
  }, []);

  // Memoized createDialog function
  const createDialog = useCallback(
    (dialogId: string, data: DialogInput) => {
      setDialogs((oldDialogs) => ({
        ...oldDialogs, // Fix: Spread first, then add new dialog
        [dialogId]: {
          uuid: dialogId,
          close: () => closeDialog(dialogId),
          ...data,
        },
      }));
    },
    [closeDialog],
  );

  // Memoized removeDialog function
  const removeDialog = useCallback((dialogId: string) => {
    setDialogs((oldDialogs) => {
      const copy = { ...oldDialogs };
      delete copy[dialogId];
      return copy;
    });
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      depth,
      dialogs,
      createDialog,
      removeDialog,
    }),
    [depth, dialogs, createDialog, removeDialog],
  );

  // Memoized dialog entries to prevent re-computation on every render
  const dialogEntries = useMemo(() => Object.entries(dialogs), [dialogs]);

  return (
    <DialogContext value={contextValue}>
      {children}
      {dialogEntries.map(([uuid, dialog]) => {
        const { Component, props, ...rest } = dialog;

        return (
          <DialogStateContext
            value={{
              ...rest,
              depth,
              remove: () => removeDialog(uuid),
            }}
            key={uuid}
          >
            <Suspense fallback={<DialogSpinner />}>
              <Component {...props} />
            </Suspense>
          </DialogStateContext>
        );
      })}
    </DialogContext>
  );
}
